import { applyParams } from "../params";
import { SortOrder } from "./generated-types/v1/Api";

type FetchCompatible = (request: Request) => Promise<Response>;

export type PaginatedResponse<T = unknown> = {
  items: T[];
  nextPage?: string;
};

export type PaginationOptions = {
  continuationToken?: string;
  order?: SortOrder;
  orderBy?: string;
  pageSize?: number;
};

export function applyPagination(
  params: URLSearchParams,
  options: PaginationOptions
) {
  applyParams(params, {
    pageSize: options.pageSize,
    order: options.order,
    orderBy: options.orderBy,
    continuationToken: options.continuationToken,
  } as PaginationOptions);
}

function isPaginatedResponse(value: unknown): value is PaginatedResponse {
  if (value && typeof value !== "object") {
    return false;
  }

  const maybePaged = value as Partial<PaginatedResponse>;
  if (!Array.isArray(maybePaged.items)) {
    return false;
  }

  return true;
}

export async function exhaust<T>(
  fetch: FetchCompatible,
  sourceRequest: Request
) {
  const buffer: PaginatedResponse<T> = {
    items: [],
  };

  const sourceURL = new URL(sourceRequest.url);

  const pageSize = sourceURL.searchParams.get("pageSize");

  let remaining = pageSize ? parseFloat(pageSize) : 1;
  let queuedRequest = sourceRequest;

  while (remaining > 0) {
    const response = await fetch(queuedRequest);

    const data = await response.json();
    if (!isPaginatedResponse(data)) {
      throw new Error("Response not compatible with PaginatedResponse");
    }

    remaining -= data.items.length;
    buffer.items.push(...(data.items as T[]));
    buffer.nextPage = data.nextPage;

    if (!buffer.nextPage) {
      break;
    }

    const nextURL = sourceURL;
    const params = sourceURL.searchParams;
    params.set("continuationToken", buffer.nextPage);
    params.set("pageSize", remaining.toString());
    queuedRequest = new Request(nextURL.toString(), sourceRequest);
  }

  return buffer;
}
